// src/pages/Testimonials.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { TestimonialCard } from '@/components/TestimonialCard';
import { Button } from '@/components/ui/button';
import { 
  testimonials,
  getFeaturedTestimonials,
  testimonialsStats,
  type Testimonial
} from '@/data/mock/testimonials';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Quote,
  Star,
  Users,
  Award,
  Heart,
  TrendingUp,
  Sparkles,
  CheckCircle,
  MessageCircle,
  Trophy,
  Target,
  Filter,
  X,
  BarChart3,
  ThumbsUp,
  Smile,
  MessageSquare
} from 'lucide-react';

const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [selectedType, setSelectedType] = useState<'all' | 'parent' | 'player' | 'coach' | 'partner' | 'alumni'>('all');
  const [showOnlyVerified, setShowOnlyVerified] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number>(0);

  // Type options
  const types = [
    { 
      value: 'all' as const, 
      label: { en: 'All', ar: 'الكل' },
      icon: MessageCircle
    },
    { 
      value: 'parent' as const, 
      label: { en: 'Parents', ar: 'الأهل' },
      icon: Users
    },
    { 
      value: 'player' as const, 
      label: { en: 'Players', ar: 'اللاعبون' },
      icon: Trophy
    },
    { 
      value: 'coach' as const, 
      label: { en: 'Coaches', ar: 'المدربون' },
      icon: Target
    },
    { 
      value: 'alumni' as const, 
      label: { en: 'Alumni', ar: 'الخريجون' },
      icon: Award
    },
    { 
      value: 'partner' as const, 
      label: { en: 'Partners', ar: 'الشركاء' },
      icon: Heart
    },
  ];

  // Filter testimonials
  const filteredTestimonials = useMemo(() => {
    return testimonials.filter(testimonial => {
      const matchesType = selectedType === 'all' || testimonial.type === selectedType;
      const matchesVerified = !showOnlyVerified || testimonial.verified;
      const matchesRating = selectedRating === 0 || testimonial.rating >= selectedRating;
      
      return matchesType && matchesVerified && matchesRating;
    });
  }, [selectedType, showOnlyVerified, selectedRating]);

  // Featured testimonials
  const featuredTestimonials = getFeaturedTestimonials();

  // Statistics
  const stats = [
    {
      icon: MessageSquare,
      value: testimonials.length.toString(),
      label: { en: 'Total Testimonials', ar: 'إجمالي الشهادات' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: CheckCircle,
      value: testimonialsStats.verified.toString(),
      label: { en: 'Verified Reviews', ar: 'مراجعات موثقة' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Star,
      value: testimonialsStats.averageRating.toFixed(1),
      label: { en: 'Average Rating', ar: 'متوسط التقييم' },
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: ThumbsUp,
      value: `${Math.round((testimonialsStats.fiveStars / testimonials.length) * 100)}%`,
      label: { en: '5-Star Reviews', ar: 'تقييمات 5 نجوم' },
      color: 'from-purple-500 to-pink-500'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('testimonials.title')}
        subtitle={isRTL
          ? 'اكتشف ما يقوله الأهالي، اللاعبون، والمدربون عن تجربتهم مع أكاديمية تشامبيونز'
          : 'Discover what parents, players, and coaches say about their experience with Champions Academy'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.testimonials') }
        ]}
      />

      {/* Trust Banner */}
      <section className="py-6 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border-y border-green-500/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center flex-wrap"
          >
            <CheckCircle className="h-6 w-6 text-green-500" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? `${testimonialsStats.verified} شهادة موثقة من عائلات ورياضيين حقيقيين`
                : `${testimonialsStats.verified} verified testimonials from real families and athletes`
              }
            </p>
            <CheckCircle className="h-6 w-6 text-green-500" />
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <BarChart3 className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'إحصائيات' : 'Statistics'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'موثوق به من قبل المئات' : 'Trusted by Hundreds'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      {featuredTestimonials.length > 0 && (
        <section className="py-20 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'شهادات مميزة' : 'Featured Testimonials'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'قصص تلهمنا' : 'Stories That Inspire Us'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {featuredTestimonials.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.2 }}
                >
                  <TestimonialCard
                    quote={testimonial.content[isRTL ? 'ar' : 'en']}
                    author={testimonial.name[isRTL ? 'ar' : 'en']}
                    role={testimonial.role[isRTL ? 'ar' : 'en']}
                    image={testimonial.image}
                    rating={testimonial.rating}
                    date={testimonial.date}
                    location={testimonial.location?.[isRTL ? 'ar' : 'en']}
                    verified={testimonial.verified}
                    featured={testimonial.featured}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1 sticky top-20 z-40 border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Type Filters */}
            <div className="flex justify-center gap-2 mb-6 flex-wrap">
              {types.map((type) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.value}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedType(type.value)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all font-medium ${
                      selectedType === type.value
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'bg-card border-2 border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {type.label[isRTL ? 'ar' : 'en']}
                  </motion.button>
                );
              })}
            </div>

            {/* Additional Filters */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {/* Verified Filter */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowOnlyVerified(!showOnlyVerified)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                  showOnlyVerified
                    ? 'bg-green-500 text-white'
                    : 'bg-card border border-border hover:border-green-500/50'
                }`}
              >
                <CheckCircle className="h-4 w-4" />
                {isRTL ? 'موثقة فقط' : 'Verified Only'}
              </motion.button>

              {/* Rating Filter */}
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
                <Star className="h-4 w-4 text-yellow-500" />
                <select
                  value={selectedRating}
                  onChange={(e) => setSelectedRating(Number(e.target.value))}
                  className="bg-transparent text-sm font-medium outline-none cursor-pointer"
                >
                  <option value={0}>{isRTL ? 'جميع التقييمات' : 'All Ratings'}</option>
                  <option value={5}>{isRTL ? '5 نجوم فقط' : '5 Stars Only'}</option>
                  <option value={4}>{isRTL ? '4+ نجوم' : '4+ Stars'}</option>
                </select>
              </div>

              {/* Clear Filters */}
              {(selectedType !== 'all' || showOnlyVerified || selectedRating > 0) && (
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedType('all');
                    setShowOnlyVerified(false);
                    setSelectedRating(0);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20 transition-all text-sm"
                >
                  <X className="h-4 w-4" />
                  {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
                </motion.button>
              )}
            </div>

            {/* Results Count */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center"
            >
              <span className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredTestimonials.length} من ${testimonials.length}`
                  : `Showing ${filteredTestimonials.length} of ${testimonials.length}`
                }
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            {filteredTestimonials.length > 0 ? (
              <motion.div
                key={`${selectedType}-${showOnlyVerified}-${selectedRating}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredTestimonials.map((testimonial, idx) => (
                  <TestimonialCard
                    key={testimonial.id}
                    quote={testimonial.content[isRTL ? 'ar' : 'en']}
                    author={testimonial.name[isRTL ? 'ar' : 'en']}
                    role={testimonial.role[isRTL ? 'ar' : 'en']}
                    image={testimonial.image}
                    rating={testimonial.rating}
                    date={testimonial.date}
                    location={testimonial.location?.[isRTL ? 'ar' : 'en']}
                    verified={testimonial.verified}
                    featured={testimonial.featured}
                    delay={idx * 0.05}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20"
              >
                <MessageCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-lg text-muted-foreground">
                  {isRTL ? 'لم يتم العثور على شهادات' : 'No testimonials found'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/30 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Icon */}
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent p-1 mb-8 shadow-2xl"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Smile className="h-12 w-12 text-primary" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'شارك تجربتك معنا!' : 'Share Your Experience!'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {isRTL
                ? 'رأيك يهمنا! شارك تجربتك مع أكاديمية تشامبيونز وساعدنا في إلهام المزيد من العائلات'
                : 'Your voice matters! Share your Champions Academy experience and help us inspire more families'
              }
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-12 py-7 text-lg rounded-full btn-glow">
                <MessageSquare className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'اكتب شهادتك' : 'Write a Testimonial'}
              </Button>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Users className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'انضم للأكاديمية' : 'Join the Academy'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
